import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faSun,
  faMoon,
  faCloud,
  faCloudSun,
  faCloudMoon,
  faCloudRain,
  faBolt,
  faSnowflake,
  faSmog,
} from '@fortawesome/free-solid-svg-icons';

export type ForecastDay = {
  weekdayShort: string;
  minF: number;
  maxF: number;
  main: string;
  iconCode: string;
};

export type LocationForecastBlock = {
  query: string;
  label: string;
  tzOffsetSec: number;
  days: ForecastDay[];
  error?: string;
};

function utcToLocalDateKey(utcSeconds: number, offsetSec: number): string {
  const shiftedMs = utcSeconds * 1000 + offsetSec * 1000;
  const d = new Date(shiftedMs);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function localHourFromUtc(utcSeconds: number, offsetSec: number): number {
  const sec = utcSeconds + offsetSec;
  return Math.floor(((sec % 86400) + 86400) % 86400 / 3600);
}

function weekdayShortFromUtc(utcSeconds: number, offsetSec: number): string {
  const ms = utcSeconds * 1000 + offsetSec * 1000;
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(new Date(ms));
}

export function formatLocalTimeFromOffset(epochMs: number, offsetSec: number): string {
  const sec = Math.floor(epochMs / 1000) + offsetSec;
  const daySec = ((sec % 86400) + 86400) % 86400;
  let h = Math.floor(daySec / 3600);
  const min = Math.floor((daySec % 3600) / 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(min).padStart(2, '0')} ${ampm}`;
}

export function weatherIconFor(main: string, iconCode: string): IconDefinition {
  const m = main.toLowerCase();
  const ic = (iconCode || '').toLowerCase();
  if (m.includes('thunder')) return faBolt;
  if (m.includes('snow')) return faSnowflake;
  if (m.includes('rain') || m.includes('drizzle')) return faCloudRain;
  if (m.includes('mist') || m.includes('fog') || m.includes('haze') || m.includes('smoke')) return faSmog;
  if (m.includes('clear')) return ic.endsWith('n') ? faMoon : faSun;
  if (m.includes('cloud')) {
    if (ic.startsWith('02')) return ic.endsWith('n') ? faCloudMoon : faCloudSun;
    return faCloud;
  }
  return faCloud;
}

export function aggregateForecast(raw: unknown, maxDays = 5): { tzOffsetSec: number; days: ForecastDay[] } {
  const o = raw as Record<string, unknown> | null;
  const tzOffsetSec = Number((o?.city as { timezone?: number } | undefined)?.timezone ?? 0);
  const list = o?.list;
  if (!Array.isArray(list) || !list.length) {
    return { tzOffsetSec, days: [] };
  }

  type Group = {
    min: number;
    max: number;
    items: Array<{ dt: number; main: string; icon: string }>;
  };
  const groups = new Map<string, Group>();

  for (const item of list) {
    const row = item as Record<string, unknown>;
    const dt = Number(row?.dt);
    if (!Number.isFinite(dt)) continue;
    const dateKey = utcToLocalDateKey(dt, tzOffsetSec);
    const mainBlock = row?.main as Record<string, unknown> | undefined;
    const tmin = Number(mainBlock?.temp_min ?? mainBlock?.temp);
    const tmax = Number(mainBlock?.temp_max ?? mainBlock?.temp);
    const w0 = Array.isArray(row?.weather) ? (row.weather as unknown[])[0] : null;
    const w = (w0 || {}) as Record<string, unknown>;
    const main = String(w?.main ?? 'Clouds');
    const icon = String(w?.icon ?? '');

    const g = groups.get(dateKey);
    if (!g) {
      groups.set(dateKey, { min: tmin, max: tmax, items: [{ dt, main, icon }] });
    } else {
      g.min = Math.min(g.min, tmin);
      g.max = Math.max(g.max, tmax);
      g.items.push({ dt, main, icon });
    }
  }

  const keys = [...groups.keys()].sort();
  const nowSec = Math.floor(Date.now() / 1000);
  const todayKey = utcToLocalDateKey(nowSec, tzOffsetSec);
  let startIdx = keys.findIndex((k) => k >= todayKey);
  if (startIdx < 0) startIdx = 0;

  const sliceKeys = keys.slice(startIdx, startIdx + maxDays);
  const days: ForecastDay[] = sliceKeys.map((dateKey) => {
    const g = groups.get(dateKey)!;
    let best = g.items[0];
    let bestScore = Infinity;
    for (const it of g.items) {
      const hour = localHourFromUtc(it.dt, tzOffsetSec);
      const score = Math.abs(hour - 12);
      if (score < bestScore) {
        bestScore = score;
        best = it;
      }
    }
    return {
      weekdayShort: weekdayShortFromUtc(best.dt, tzOffsetSec),
      minF: Math.round(g.min),
      maxF: Math.round(g.max),
      main: best.main,
      iconCode: best.icon,
    };
  });

  return { tzOffsetSec, days };
}
